// Extend expiry / Extend and reactivate (architecture sprint-06 §6.3, F74). The link stops working at the
// end of the chosen day in the acting Admin's timezone; the earliest date is that Admin's today.
import { addDays, type LocalDate } from '@shared/calendar';
import { DEFAULT_EXTEND_DAYS, isValidExtendDate, MAX_EXTEND_DATE } from '@shared/share-link-rules';
import { useId, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const EXTEND_PAST_MESSAGE = 'Pick today or a later date.';

interface ExtendExpiryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expired: boolean;
  today: LocalDate;
  timezone: string;
  pending: boolean;
  onSubmit: (expiresOn: LocalDate) => void;
}

export function ExtendExpiryDialog(props: ExtendExpiryDialogProps): JSX.Element {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      {/* Remount the form on every open so the default date is recomputed from today. */}
      <DialogContent>{props.open ? <ExtendForm {...props} /> : null}</DialogContent>
    </Dialog>
  );
}

function ExtendForm({ expired, today, timezone, pending, onSubmit, onOpenChange }: ExtendExpiryDialogProps): JSX.Element {
  const id = useId();
  const [value, setValue] = useState<LocalDate>(() => addDays(today, DEFAULT_EXTEND_DAYS));
  const valid = isValidExtendDate(value, today);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (valid && !pending) onSubmit(value);
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <DialogHeader>
        <DialogTitle>{expired ? 'Extend and reactivate' : 'Extend expiry'}</DialogTitle>
        <DialogDescription>
          {expired
            ? `This link has expired. Choosing a date makes it work again, for anyone who has it, until the end of that day (${timezone}).`
            : `The link will stop working at the end of the chosen day (${timezone}).`}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-date`}>Works until the end of</Label>
        <Input
          id={`${id}-date`}
          type="date"
          min={today}
          max={MAX_EXTEND_DATE}
          value={value}
          aria-invalid={!valid}
          aria-describedby={valid ? undefined : `${id}-error`}
          onChange={(e) => setValue(e.target.value)}
        />
        {valid ? null : (
          <p id={`${id}-error`} role="alert" className="text-xs text-danger-fg">
            {EXTEND_PAST_MESSAGE}
          </p>
        )}
      </div>
      <DialogFooter>
        <Button type="submit" className="flex-1" disabled={!valid || pending}>
          {pending ? 'Saving…' : 'Extend'}
        </Button>
        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
      </DialogFooter>
    </form>
  );
}
